import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { validateToken } from '../services/authService';
import AccessDenied from '../components/Feedback/AccessDenied';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [authenticated, setAuthenticated] = useState(false);
  const [isAuthLoading, setIsAuthLoading] = useState(true);
  const [isAccessDenied, setIsAccessDenied] = useState(false);
  const [user, setUser] = useState(null);
  const [roles, setRoles] = useState([]);

  // EG-46: AVALIADOR entra no painel — a auditoria saiu do portal do respondente
  // e passou a viver aqui, então o auditor não tem mais onde trabalhar se for
  // barrado (antes caía em AccessDenied + logout).
  const hasValidRole = useCallback(() => {
    const userRoles = user?.user?.roles || [];
    return userRoles.some(
      role =>
        role.name === 'MASTER' ||
        role.name === 'RESPONSAVEL' ||
        role.name === 'AVALIADOR'
    );
  }, [user?.user?.roles]);

  const hasMasterRole = useCallback(() => {
    return user?.user?.roles?.some(role => role.name === 'MASTER') || false;
  }, [user?.user?.roles]);

  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem('access_token');
    const email = localStorage.getItem('userEmail');
    
    if (token && email) {
      try {
        const result = await validateToken();
        setAuthenticated(result.valid);
        if (result.user) {
          setUser({ user: result.user });
          localStorage.setItem('userEmail', result.user.email);
        } else {
          setAuthenticated(false);
          setUser(null);
        }
      } catch {
        setAuthenticated(false);
        setUser(null);
      }
    } else {
      // Sem token ou email - marca como não autenticado
      setAuthenticated(false);
      setUser(null);
    }
    
    setIsAuthLoading(false);
  }, []);

  const login = useCallback((loginData) => {
    localStorage.setItem('access_token', loginData.access_token);
    localStorage.setItem('refresh_token', loginData.refresh_token);
    localStorage.setItem('userEmail', loginData.user.email);
    setUser({ user: loginData.user });
    setAuthenticated(true);
    setIsAuthLoading(false);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('userEmail');
    setAuthenticated(false);
    setUser(null);
    // Redirecionar para a página de login
    window.location.href = '/login';
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  useEffect(() => {
    if (authenticated && user) {
      setRoles(user.user.roles || []);
      if (!hasValidRole()) {
        setIsAccessDenied(true);
        const timer = setTimeout(() => logout(), 5000);
        return () => clearTimeout(timer);
      } else {
        setIsAccessDenied(false);
      }
    }
  }, [authenticated, user, hasValidRole, logout]);

  const value = {
    user,
    authenticated,
    isAuthLoading,
    login,
    logout,
    hasValidRole,
    hasMasterRole,
  };

  if (isAccessDenied) {
    return <AccessDenied type={hasMasterRole() ? 'company' : 'admin'} />;
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useSSO() {
  return useContext(AuthContext);
}